import {convertCamelCaseToSpaceDelimited, convertUnderscoreToDash, isNullOrUndefined} from "../commonUtilities";
import {isArrayNullOrEmpty} from "./arrayUtilities";
import {objectHasProperty} from "./objectUtilities";
import {isArray, isString} from "./typeUtilities";

export interface ListItem {
    text: string;
    value: string | number;
    id?: string | number;
}

export interface DescriptionObject {
    id: string | number;
    description: string;
}

export function isInSelectList(list: ListItem[], string: string): boolean {
    return !!list.find((item) => item.value === string || item.text === string);
}

export function createDescriptionObjectForList(id: string | number, description: string): DescriptionObject {
    return {
        id,
        description
    };
}

export function createListFromArray(array: string[]): ListItem[] {
    let list: ListItem[] = [];

    if (isArrayNullOrEmpty(array)) return list;

    array.map((item) => {
        return list.push(createListItem(item, convertCamelCaseToSpaceDelimited(item)));
    });

    return list;
}

export const createListFromArrayOfObjects = (array: Record<string, string | number | unknown>[] = [], valueProperty = "", textProperty = ""): ListItem[] => {
let list: ListItem[] = [];

if (isArray(array)) {
    for (const item of array) {
        if (objectHasProperty(item, valueProperty) && objectHasProperty(item, textProperty))
            list.push(createListItem(item[valueProperty] as string | number, item[textProperty] as string | number));
        }
    }
    return list;
};

export function createListFromObject(object: Record<string, string | number | { id?: string | number; description?: string }>, usePropertyValueForText = false, hasZeroAsId = false): ListItem[] {
    let result: ListItem[] = [];
    for (let property in object) {
        if (objectHasProperty(object, property)) {
            const item = object[property];
            const isComplexItem = typeof item === 'object' && item !== null;
            const value = (isComplexItem && (item.id || hasZeroAsId)) ? item.id as string | number : isComplexItem ? '' : item;
            const id = (isComplexItem && (item.id || hasZeroAsId)) ? item.id as string | number : isComplexItem ? property : property;
            const listText = usePropertyValueForText ? String(item) : convertPropertyNames(property);
            const description = (isComplexItem && item.description) ? item.description : listText;

            result.push(createListItem(value, description, id));
        }
    }
    return result;
}

export function createListFromObjectWhereValueIsPropertyName(object: Record<string, string | number>): ListItem[] {
    let result: ListItem[] = [];
    for (let property in object) {
        if (objectHasProperty(object, property)) {
            result.push(createListItem(property, object[property], property));
        }
    }
    return result;
}

function convertPropertyNames(property: string): string {
    property = convertCamelCaseToSpaceDelimited(property);
    property = convertUnderscoreToDash(property);
    return property;
}

export function createListItem(value: string | number, text?: string | number, id?: string | number): ListItem {
    return {
        text: text ? String(text) : String(value),
        value: value,
        id: id ? id : value,
    };
}

export const createArchivableListFromArray = (items: string[] = [], value: string | number): ListItem[] => {
    let list: ListItem[] = [];

    if (isString(value) && value.length > 1 && !items.includes(value)) list.push(createListItem(value, `${value} (archived)`));

    if (isArray(items))
        items.map((item) => {
            return list.push(createListItem(item));
        });
    return list;
};

export function getTextByValue(list: ListItem[], value: string | number | null | undefined): string {
    value = isNullOrUndefined(value) ? 0 : value;
    const result = list.find(l => l.value.toString() === value.toString());
    return result ? result.text : "";
}

export function createListIfIdArray(selectedItemsIds: (string | number)[] | null | undefined, fullList: ListItem[]): ListItem[] {
    if (!selectedItemsIds) return [];

    let list: ListItem[] = [];

    if (fullList) {
        for (let listItem of fullList) {
            const itemExists = selectedItemsIds.find(f => f === listItem.id);
            if (!itemExists)
                continue;

            const selectListItem = createListItem(listItem.id ?? listItem.value, listItem.text);
            list.push(selectListItem);
        }
    }

    return list;
}

export function getObjectFromObjectId<T extends Record<string, unknown>>(id: string | number, objectList: T[], objectId = "id"): T | undefined {
    return objectList.find(o => o[objectId] === id);
}
